'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { GraduationCap, Shield, Users, Zap, ArrowRight, CheckCircle, Code, Globe } from 'lucide-react'
import { projectsApi } from '@/lib/api'
import type { Project } from '@/lib/types'

// Fallback static data for when API is unavailable
const fallbackProducts = [
  {
    id: 'sparky-ventures',
    name: 'Sparky Ventures',
    tagline: 'Business-Skills Platform for Families',
    description: 'A platform for families, in development, where parents help children aged 8–15 learn business basics.',
    icon: GraduationCap,
    color: 'from-blue-500 to-cyan-500',
    features: [
      'Parents have their own account, linked to their child\'s, and approve opening a shop.'
    ],
    status: 'in_development'
  },
  {
    id: 'rebelverify',
    name: 'RebelVerify',
    tagline: 'Privacy-First Age Verification',
    description: 'Privacy-first age verification in development, designed so sites don\'t need to see your date of birth.',
    icon: Shield,
    color: 'from-purple-500 to-pink-500',
    features: [
      'Privacy-first design'
    ],
    status: 'in_development'
  }
]

export default function Products() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await projectsApi.getAll({ featured: true })
        
        if (error) throw new Error(error)
        setProjects(data || [])
      } catch (err) {
        console.warn('Failed to fetch projects from API, using fallback data:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch projects')
      } finally {
        setLoading(false)
      }
    }

    fetchProjects()
  }, [])

  const getIconForProject = (title: string, techStack?: string[]) => {
    const lowerTitle = title.toLowerCase()
    if (lowerTitle.includes('sparky') || lowerTitle.includes('marketplace') || lowerTitle.includes('learning')) {
      return GraduationCap
    }
    if (lowerTitle.includes('rebel') || lowerTitle.includes('verify') || lowerTitle.includes('age')) {
      return Shield
    }
    if (techStack?.some(tech => tech.toLowerCase().includes('web'))) {
      return Globe
    }
    return Code
  }

  const getColorForProject = (title: string, index: number) => {
    const lowerTitle = title.toLowerCase()
    if (lowerTitle.includes('sparky') || lowerTitle.includes('marketplace')) {
      return 'from-blue-500 to-cyan-500'
    }
    if (lowerTitle.includes('rebel') || lowerTitle.includes('verify')) {
      return 'from-purple-500 to-pink-500'
    }
    
    const colors = [
      'from-green-500 to-emerald-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-blue-500',
      'from-pink-500 to-rose-500'
    ]
    return colors[index % colors.length]
  }

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  // Use API data if available, otherwise fallback to static data
  const displayProducts = projects.length > 0 ? projects : (error ? fallbackProducts : [])
  return (
    <section id="products" className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Our <span className="gradient-text">Product Portfolio</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Privacy-first technology.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : displayProducts.length > 0 ? (
          <div className="space-y-20">
            {displayProducts.map((product: any, index: number) => {
              const IconComponent = getIconForProject(product.title || product.name, product.tech_stack)
              const color = getColorForProject(product.title || product.name, index)
              const isApiData = 'title' in product // Check if it's API data vs fallback data
              
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}
                >
                  {/* Product Info */}
                  <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <div className="space-y-4">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${color}`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      
                      <div>
                        <h3 className="text-3xl font-bold text-gray-800 mb-2">
                          {isApiData ? product.title : product.name}
                        </h3>
                        <p className="text-lg text-primary-600 font-medium mb-4">
                          {isApiData ? product.short_description || formatStatus(product.status) : product.tagline}
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                          {product.description}
                        </p>
                      </div>
                    </div>

                    {/* Tech Stack or Features */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-800">
                        {isApiData ? 'Technology Stack' : 'Key Features'}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(isApiData ? product.tech_stack : product.features)?.map((item: string, itemIndex: number) => (
                          <div key={itemIndex} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{item}</span>
                          </div>
                        )) || (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm text-gray-600">Innovative technology solution</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status and CTA */}
                    <div className="space-y-4">
                      {isApiData && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Status:</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            product.status === 'live' ? 'bg-green-100 text-green-800' :
                            product.status === 'beta' ? 'bg-blue-100 text-blue-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {formatStatus(product.status)}
                          </span>
                        </div>
                      )}
                      
                      {product.demo_url ? (
                        <motion.a
                          href={product.demo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${color} text-white font-semibold rounded-2xl hover:shadow-lg transition-all duration-200`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Visit Website
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </motion.a>
                      ) : product.github_url ? (
                        <motion.a
                          href={product.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${color} text-white font-semibold rounded-2xl hover:shadow-lg transition-all duration-200`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          View Code
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </motion.a>
                      ) : (
                        <motion.div
                          className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${color} text-white font-semibold rounded-2xl opacity-75 cursor-not-allowed`}
                        >
                          {product.status === 'live' ? 'Available' : 'In Development'}
                          <ArrowRight className="w-4 h-4 ml-2 opacity-50" />
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Product Visualization */}
                  <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                    <motion.div
                      animate={{ y: [0, -10, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: index }}
                      className={`bg-gradient-to-br ${color} rounded-3xl p-8 shadow-2xl`}
                    >
                      <div className="bg-white rounded-2xl p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-800">
                            {isApiData ? product.title : product.name}
                          </h4>
                          <div className={`w-3 h-3 rounded-full ${
                            product.status === 'live' ? 'bg-green-400' :
                            product.status === 'beta' ? 'bg-blue-400' :
                            'bg-orange-400'
                          }`}></div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="bg-gradient-to-r from-gray-100 to-blue-100 rounded-xl p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">
                                {isApiData ? 'Project Status' : 'Development Stage'}
                              </span>
                              <IconComponent className="w-4 h-4 text-blue-600" />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              {isApiData ? formatStatus(product.status) : 'In Development'}
                            </p>
                          </div>
                          {product.tech_stack && product.tech_stack.length > 0 && (
                            <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Technology</span>
                                <Code className="w-4 h-4 text-green-600" />
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                {product.tech_stack.slice(0, 2).join(', ')}
                                {product.tech_stack.length > 2 ? '...' : ''}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>

                    {/* Floating Elements */}
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className={`absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br ${color} rounded-full flex items-center justify-center shadow-lg`}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </motion.div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Code className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Projects Available</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              We&apos;re currently building our product portfolio. Check back soon for updates on our innovative solutions.
            </p>
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Partner with Us?</h3>
            <p className="text-lg mb-6 opacity-90">
              Discover how our innovative solutions can transform your business.
            </p>
            <motion.a
              href="#contact"
              className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-2xl hover:bg-gray-50 transition-colors inline-flex items-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Our Team
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
